import { fetchFromSMP, possibleMethods as fetchFromSMPMethods } from './SMPFetcher';
import { objectTypeToApiPath, ApiFunctions, ObjectTypes, ApiPaths } from './consts';
import { buildAPIRequestPath } from '../utils/utils';

/**
 * SMPFetchHelper
 * All API requests are configured here, and exported as Api object.
 */

const Api = {
    // Session
    login: loginSMP,
    confirmTwoFA: confirmTwoFA,
    renewUserPassword: renewUserPassword,
    logout: logoutSMP,
    changePortal: changePortal,
    getSessionData: getSessionData,
    getLicenseStatus: getLicenseStatus,
    // General
    fetchSingleObject: fetchSMPSingleObject,
    fetchObjectListFromDomains: fetchSMPObjectListFromDomains,
    fetchObjectList: fetchSMPObjectList,
    getObjectsCount: getSMPObjectsCount,
    updateSingleObject: updateSMPSingleObject,
    verifySingleObject: verifySMPSingleObject,
    deleteObjects: deleteSMPObjects,
    createObject: createSMPObject,
    cloneObject: cloneSMPObject,
    getUserType: getUserType,
    getOverviewNoticeMessage: getOverviewNoticeMessage,
    getCaCert: getCaCert,
    getTemplatePreview: getTemplatePreview,
    getAlertPreview: getAlertPreview,
    getAlertLogIds: getAlertLogIds,
    getTemplateDefault: getTemplateDefault,
    getAlertDefault: getAlertDefault,
    doAction: doAction,
    getSessions: getSessions,
    sendUserTwoFAMail: sendUserTwoFAMail,
    reinitializeSystemCaStart: reinitializeSystemCaStart,
    reinitializeSystemCaGetProgress: reinitializeSystemCaGetProgress,
    reinitializeSystemCaStop: reinitializeSystemCaStop,
    requestNewServiceDomain: requestNewServiceDomain,
    // Gateway
    fetchServiceDomainGatewayCustomFieldsNames: fetchServiceDomainGatewayCustomFieldsNames,
    gatewayGetUsedFirmwaresList: gatewayGetUsedFirmwaresList,
    gatewaySeamlessLogin: gatewaySeamlessLogin,
    getGatewaysStatus: getGatewaysStatus,
    resetToPlan: resetToPlan,
    moveToPlan: moveToPlan,
    getGatewayMailToOwner: getGatewayMailToOwner,
    sendGatewayMailToOwner: sendGatewayMailToOwner,
    validateAccessPolicy: validateAccessPolicy,
    validateSSLCustomSite: validateSSLCustomSite,
    validateTPExceptionsWhitelist:validateTPExceptionsWhitelist,
    manageRevokeGatewayCertificate: manageRevokeGatewayCertificate,
    getGatewayCert: getGatewayCert,
    // User
    getUserMailToUsers: getUserMailToUsers,
    sendUserMailToUsers: sendUserMailToUsers,
    validateUserPasswordField: validateUserPasswordField,
    validateSysUserPasswordField: validateSysUserPasswordField,
    validateAdminUserPasswordField: validateAdminUserPasswordField,
    // Community
    getCommunitySummaryMail: getCommunitySummaryMail,
    sendCommunitySummaryMail: sendCommunitySummaryMail,
    getCommunityMembers: getCommunityMembers,
    getPossibleCommunityMembersList: getPossibleCommunityMembersList,
    addGatewaysToCommunityMembers: addGatewaysToCommunityMembers,
    addCommunityToCommunityMembers: addCommunityToCommunityMembers,
    removeCommunityMembers: removeCommunityMembers,
    // Excel
    toExcelStart: toExcelStart,
    toExcelGetProgress: toExcelGetProgress,
    toExcelDone: toExcelDone,
    logsToExcelStart: logsToExcelStart,
    logsToExcelGetProgress: logsToExcelGetProgress,
    logsToExcelDone: logsToExcelDone,
    generateDomainReports: generateDomainReports,
    generateSystemReports: generateSystemReports,
    // Export SMP
    exportDBFileStart: exportDBFileStart,
    exportDBFileGetProgress: exportDBFileGetProgress,
    exportDBFileStop: exportDBFileStop,
    exportDBFileDone: exportDBFileDone,
    // Export SMP
    importDBFileStart: importDBFileStart,
    importDBFileGetProgress: importDBFileGetProgress,
    importDBFileStop: importDBFileStop,
    importDBFileDone: importDBFileDone,
    // Export Domain
    exportDomainDBFileStart: exportDomainDBFileStart,
    // Import Domain
    importDomainDBFileStart: importDomainDBFileStart,
    // Firmware
    getDcFirmwareList: getDcFirmwareList,
    getFirmwareListByVersion: getFirmwareListByVersion,
    createFirmwares: createFirmwares,
    createPrivateFirmware: createPrivateFirmware,
    addPrivateFirmwareToThisPortal: addPrivateFirmwareToThisPortal,
    SetFirmwareCustomerHotfixToGA: SetFirmwareCustomerHotfixToGA,
    SetFirmwareAsRecommended: SetFirmwareAsRecommended,
    // Image
    createImage: createImage,
    uploadReportCustomLogo: uploadReportCustomLogo,
    readImageData: readImageData,
    // Appi
    getAppiOtherApplicationsList: getAppiOtherApplicationsList,
    //Updateable Objects
    getUpdatableObjects: getUpdatableObjects,
    //Zones
    getZoneList: getZoneList,
    // Cyber-views
    getSmartviewDataUrl: getSmartviewDataUrl,
    // Service domain settings
    createCaCertificate: createCaCertificate,
    deleteCaCertificate: deleteCaCertificate,
    testRadiusServer: testRadiusServer,
    // System settings
    getSystemOverviewData: getSystemOverviewData,
    getLicenseManagementList: getLicenseManagementList,
    setLicenseManagementList: setLicenseManagementList,
    // Logs
    cancelTasks: cancelTasks,
    clearFinishedTasksFromActivityLogs: clearFinishedTasksFromActivityLogs,
}

export default Api;

/**
 * Login with SMP user credentials and service domain
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      id: <String: Id of new session>  
 *   }
 */
export function loginSMP(
    serviceDomain,
    username,
    password
) {
    return fetchFromSMP(
        'login',
        fetchFromSMPMethods.POST,
        {
            data: {
                serviceDomain,
                username,
                password,
                app: "WEB-UI"
            },
            timeout: 10000 // Timeout of 10 seconds for this request
        }
    );
}

/**
 * To finish the 2FA login process
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      id: <String: Id of new session>  
 *   }
 */
export function confirmTwoFA(
    verificationCode
) {
    return fetchFromSMP(
        'login-2fa',
        fetchFromSMPMethods.POST,
        {
            data: {
                twoFactorVerificationCode: verificationCode
            }
        }
    );
}


/**
* Password renewal in login process
*
* @returns Promise with data: {
        *      message: <english-message>
        *      errorCode: <ERROR_CODE>
        *      results: 
                Object
                        errorMessage: <Array<Object<String:errorMessages>>: Results of action>  
                        successRenewingPass: boolean indicating success
        *   }
        */
export function renewUserPassword(
    password,
    password2
) {
    return fetchFromSMP(
        'login-password-renew',
        fetchFromSMPMethods.POST,
        {
            data: {
                passwordCandidate: password,
                passwordCandidate2: password2
            }
        }
    );
}

/**
 * Logout of SMP, invalidates token sent with request
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *   }
 */
export function logoutSMP() {
    return fetchFromSMP(
        'logout',
        fetchFromSMPMethods.POST,
        {
            timeout: 7000 // Timeout of 7 seconds for this request
        }
    )
}

/**
 * Change portal with same user, portal must be authorized for user to use.
 * Available portals are get from getSessionData
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      id: <String: Id of new session>  
 *   }
 */
export function changePortal(
    serviceDomain
) {
    return fetchFromSMP(
        'change-portal',
        fetchFromSMPMethods.POST,
        {
            data: {
                serviceDomain,
                app: "WEB-UI"
            }
        }
    )
}

/**
 * @typedef {Object} GetSessionDataResponse
 * @property {string} message - English message
 * @property {string} errorCode - Error code
 * @property {object} data - Date
 * @property {string} data.portalName - Name of current portal
 * @property {string} data.userId - Id of current user
 * @property {string} data.userRole - Role of current user
 * @property {boolean} data.isPortalTwoFactorAuthenticationEnabled - Is 2FA enabled for portal users
 * @property {boolean} data.isSystemTwoFactorAuthenticationEnabled - Is 2FA enabled for system users
 * @property {number} data.minimumPasswordLength - Minimum length of passwords allowed
 * @property {number} data.maximumPasswordLength - Maximum length of passwords allowed
 * @property {number} data.userRole - Role of current user
 * @property {[string]} data.availablePortals - Portals available for user to use
 * @property {[string]} data.availableServerGroups - Server Groups available for user to use
 * @property {[string]} data.availablePlans - Plans available for user to use
 * @property {[string]} data.availableRoles - Roles available for user to use
 * @property {[string]} data.availableSystemRoles - Roles available for user to use
 */
/**
 * Fetches session data from SMP  
 * @param {object} options
 * @param {[string]} options.attributes
 * 
 * @returns {Promise<GetSessionDataResponse>}
 */
export function getSessionData(options) {
    const data = {};
    if (options && options.attributes) {
        data.attributes = options.attributes;
    }
    return fetchFromSMP(
        'get-session-data',
        fetchFromSMPMethods.POST,
        {
            data: data
        }
    )
}

/**
 * @typedef {Object} GetLicenseStatusResponse
 * @property {string} message - English message
 * @property {string} errorCode - Error code
 * @property {object} data - Data
 * @property {[object]} data.dataList - List of returned data
 * @property {string} data.dataList.warningMsg - Warning message
 * @property {[any]} data.dataList.warningMsgParams - Warning message
 */
/**
 * Gets status of licenses from SMP
 * 
 * @returns {Promise<GetLicenseStatusResponse>}
 */
export function getLicenseStatus() {
    return fetchFromSMP(
        'get-license-status',
        fetchFromSMPMethods.POST,
        {
            data: {}
        }
    )
}

/**
 * Fetches single object from SMP
 * 
 * @param {ObjectTypes} objectType Object type to fetch of
 * @param {String} objectName Name of object
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      data: <Object: Object fetched>  
 *   }
 */
export function fetchSMPSingleObject(
    objectType,
    objectName
) {
    const fetchObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.GET
    ]);

    return fetchFromSMP(
        fetchObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: objectName
            }
        }
    );
}

/**
 * Fetches object list from SMP. in case the object can be under a list of domains
 * managed by multi domain admin or system user.
 * 
 * @param {ObjectTypes} objectType Object type to fetch list of
 * @param {Number} startIndex First index to fetch from
 * @param {Number} maxItems Maximum items to fetch
 * @param {String} sort Sort basis, this must be in sync with the SMP
 * @param {String} sortDirection Direction to sort, from enum SortDirections
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      fetchedAll: <Boolean: if the whole table was fetched>  
 *      nextBaseIndex: <Number: next base index to fetch more objects after this list>  
 *      data: [  
 *          ...{}  
 *      ] <ArrayOf(Objects): List of objects fetched>  
 *     }
 */
 export function fetchSMPObjectListFromDomains(
    objectType,
    startIndex,
    maxItems,
    sort,
    sortDirection,
    filter
) {
    if (!objectTypeToApiPath[objectType]) {
        throw new Error('Illegal object type for fetchSMPObjectList: ' + objectType)
    }

    const fetchListPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.GET_LIST_FROM_DOMAINS
    ]);

    return fetchFromSMP(
        fetchListPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                startIndex: startIndex || 0,
                sort: sort || undefined,
                sortDirection: sortDirection || undefined,
                maxItems: maxItems || 30000,
                filter: filter || undefined
            }
        }
    )
}

/**
 * Fetches object list from SMP
 * 
 * @param {ObjectTypes} objectType Object type to fetch list of
 * @param {Number} startIndex First index to fetch from
 * @param {Number} maxItems Maximum items to fetch
 * @param {String} sort Sort basis, this must be in sync with the SMP
 * @param {String} sortDirection Direction to sort, from enum SortDirections
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      fetchedAll: <Boolean: if the whole table was fetched>  
 *      nextBaseIndex: <Number: next base index to fetch more objects after this list>  
 *      data: [  
 *          ...{}  
 *      ] <ArrayOf(Objects): List of objects fetched>  
 *     }
 */
export function fetchSMPObjectList(
    objectType,
    startIndex,
    maxItems,
    sort,
    sortDirection,
    filter
) {
    if (!objectTypeToApiPath[objectType]) {
        throw new Error('Illegal object type for fetchSMPObjectList: ' + objectType)
    }

    const fetchListPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.GET_LIST
    ]);

    return fetchFromSMP(
        fetchListPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                startIndex: startIndex || 0,
                sort: sort || undefined,
                sortDirection: sortDirection || undefined,
                maxItems: maxItems || 30000,
                filter: filter || undefined
            }
        }
    )
}

/**
 * get gateway custom fields names
 */

 export function fetchServiceDomainGatewayCustomFieldsNames() {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.PORTAL_SETTINGS],
        'get-gw-custom-fields-names'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {},
        }
    );
}


/**
 * Gets count of all objects of certain type from SMP
 * 
 * @param {ObjectTypes} objectType Object type to get count of
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      count: <number>  
 */
export function getSMPObjectsCount(
    objectType
) {
    if (!objectTypeToApiPath[objectType]) {
        throw new Error('Illegal object type for getSMPObjectsCount: ' + objectType)
    }

    const getCountPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.GET_COUNT
    ]);

    return fetchFromSMP(
        getCountPath,
        fetchFromSMPMethods.POST,
        {
            data: {}
        }
    )
}

/**
 * Updates and saves single object to SMP
 * 
 * @param {ObjectTypes} objectType Object type to update
 * @param {String} objectName Name of object
 * @param {Object} data object to save
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      errorData: <Object: additional error data>  
 *   }
 */
export function updateSMPSingleObject(
    objectType,
    objectName,
    data
) {
    const updateObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.UPDATE
    ]);

    return fetchFromSMP(
        updateObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: objectName,
                data: data
            }
        }
    );
}

/**
 * Verifies single object
 * 
 * @param {ObjectTypes} objectType Object type to verify
 * @param {String} objectName Name of object
 * @param {Object} data object to verify
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      errorData: <Object: additional error data>  
 *   }
 */
export function verifySMPSingleObject(
    objectType,
    objectName,
    data
) {
    const updateObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.VERIFY
    ]);

    return fetchFromSMP(
        updateObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: objectName,
                data: data
            }
        }
    );
}


/**
 * Deletes objects from SMP
 * 
 * @param {ObjectTypes} objectType Object type to delete
 * @param {Array<String>} objectNameList List of object names to delete
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function deleteSMPObjects(
    objectType,
    objectNameList,
    password,
    timeout
) {
    const deleteObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.DELETE
    ]);

    return fetchFromSMP(
        deleteObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                nameList: objectNameList,
                password: password
            },
            timeout: timeout
        }
    );
}


/**
 * Create object in SMP
 * 
 * @param {ObjectTypes} objectType Object type to create
 * @param {Object} objectToCreate Object to create
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      name: <String: Name of new created object>  
 *   }
 */
export function createSMPObject(
    objectType,
    objectToCreate
) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.CREATE
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                data: objectToCreate
            },
            timeout: 30000 // Timeout of 30 seconds for this request
        }
    );
}

/**
 * Clone object in SMP
 * 
 * @param {ObjectTypes} objectType Object type to clone
 * @param {Object} objectToClone Object to clone
 * @param {String} origObjectId Original id of object to clone
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      name: <String: Name of new cloned object>  
 *   }
 */
export function cloneSMPObject(
    objectType,
    objectToClone,
    origObjectId
) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.CLONE
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                data: objectToClone,
                origName: origObjectId
            }
        }
    );
}

/**
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      overviewNoticeMessage: <String: notice message>  
 *   }
 */
export function getOverviewNoticeMessage() {
    return fetchFromSMP(
        'get-overview-notice-message',
        fetchFromSMPMethods.POST,
        {}
    );
}

/**
 * Clone object in SMP
 * 
 * @param {String} userId Id of user to check
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      name: <String: Name of new cloned object>  
 *   }
 */
export function getUserType(
    userId
) {
    return fetchFromSMP(
        ApiFunctions.GET_USER_TYPE,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: userId
            }
        }
    );
}

/**
 * Gets statuses of gateways in SMP (connected, disconected, disabled)
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      name: <String: Name of new created object>  
 *   }
 */
export function getGatewaysStatus() {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.GATEWAY],
        ApiFunctions.GET_ALL_STATUS
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
    );
}

/**
 * Gets list of firmwares used by gateways in this portal
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      data: { firmares:<[String]> }  
 *   }
 */
export function gatewayGetUsedFirmwaresList(gatewayId, forceType, page) {
    const getUsedFirmwaresPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.GATEWAY],
        'get-used-firmwares-list'
    ]);

    return fetchFromSMP(
        getUsedFirmwaresPath,
        fetchFromSMPMethods.POST,
        {}
    );
}

/**
 * Get seamless login data for a gateway
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      data: { applianceName:<String>, applianceUser:<String>, applianceTimedOut:<Number>, applianceLoginToken:<String>, appliancePage:<String>, smpLogin:<Boolean> }  
 *   }
 */
export function gatewaySeamlessLogin(gatewayId, forceType, page) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.GATEWAY],
        'seamless-login'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: gatewayId,
                forceType: forceType,
                page: page
            }
        }
    );
}


/**
 * Resets object to plan
 *
 * @param {ObjectTypes} objectType Object type to delete
 * @param {Array<String>} objectNameList List of object names to reset to plan
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function resetToPlan(objectType, objectNameList) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.RESET_TO_PLAN
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                nameList: objectNameList
            }
        }
    );
}


/**
 * Moves objects to plan
 *
 * @param {ObjectTypes} objectType Object type to delete
 * @param {Array<String>} objectNameList List of object names to reset to plan
 * @param {String} planName Plan name to move objects to
 * 
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function moveToPlan(objectType, objectNameList, planName) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.MOVE_TO_PLAN
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                nameList: objectNameList,
                planName: planName
            }
        }
    );
}

/**
 * Fetches ca cert of current service domain
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function getCaCert() {
    const createObjectPath = buildAPIRequestPath([
        ApiFunctions.GET_CA_CERT
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {},
        {'Accept': 'application/octet-stream'}
    );
    // About file download with ajax
    // https://medium.com/@drevets/you-cant-prompt-a-file-download-with-the-content-disposition-header-using-axios-xhr-sorry-56577aa706d6
    
}

/**
 * Fetches preview of message template
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function getTemplatePreview(data) {
    return fetchFromSMP(
        ApiFunctions.GET_TEMPLATE_PREVIEW,
        fetchFromSMPMethods.POST,
        {
            data: data
        }
    );
}

/**
 * Fetches preview of alert message template
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function getAlertPreview(data) {
    return fetchFromSMP(
        ApiFunctions.GET_ALERT_PREVIEW,
        fetchFromSMPMethods.POST,
        {
            data: data
        }
    );
}

/**
 * Fetches log ids of alert
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function getAlertLogIds(data) {
    return fetchFromSMP(
        'get-alert-log-id',
        fetchFromSMPMethods.POST,
        {
            data: {}
        }
    );
}

/**
 * Gets default parameters of template by name
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function getTemplateDefault(name, isGlobal) {
    return fetchFromSMP(
        'get-notifications-default',
        fetchFromSMPMethods.POST,
        {
            data: {
                name: name,
                isGlobal: isGlobal
            }
        }
    );
}

/**
 * Gets default parameters of alert by name
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function getAlertDefault(name, isGlobal) {
    return fetchFromSMP(
        'get-alert-default',
        fetchFromSMPMethods.POST,
        {
            data: {
                name: name,
                isGlobal: isGlobal
            }
        }
    );
}

/**
 * Gets default values of gateway's mail to owner
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function getGatewayMailToOwner(gatewayList) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.GATEWAY],
        'get-mail-to-owner'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                nameList: gatewayList
            }
        }
    );
}

/**
 * Sends gateway mail to owner
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function sendGatewayMailToOwner(subject, to, bcc, body, captchaKey) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.GATEWAY],
        'send-mail-to-owner'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                subject,
                to,
                bcc,
                body,
                captchaKey
            }
        }
    );
}

/**
 * Sends gateway mail to owner
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function validateAccessPolicy(objectType, fullObjectName, accessPolicyObject) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        'validate-ap-cprule'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: fullObjectName,
                data: {
                    cpRule: accessPolicyObject
                }
            }
        }
    );
}

export function validateSSLCustomSite(objectType, fullObjectName, sslCustomSiteObject) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        'validate-ssl-custom-site'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: fullObjectName,
                data: {
                    sslCustomSite: sslCustomSiteObject
                }
            }
        }
    );
}

export function validateTPExceptionsWhitelist(objectType, fullObjectName, tpExceptionsWhitelistUrl) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        'validate-tp-exceptions-allowlist-url'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: fullObjectName,
                data: {
                    tpExceptionsAllowlistUrl: tpExceptionsWhitelistUrl
                }
            }
        }
    );
}


/**
 * Sends gateway mail to owner
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function manageRevokeGatewayCertificate(name) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.GATEWAY],
        'manage-revoke-cert'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: name
            }
        }
    );
}

/**
 * Sends gateway mail to owner
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function getGatewayCert(name) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.GATEWAY],
        'get-gateway-cert'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: name
            }
        },
        {'Accept': 'application/octet-stream'}
    );
}

/**
 * Gets default values of user's mail to users
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function getUserMailToUsers(userList) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.USER],
        'get-mail-to-users'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                nameList: userList
            }
        }
    );
}

/**
 * Sends user mail to users email
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function sendUserMailToUsers(subject, to, bcc, body, captchaKey) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.USER],
        'send-mail-to-users'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                subject,
                to,
                bcc,
                body,
                captchaKey
            }
        }
    );
}

/**
 * Validates a user's password
 *
 * @returns Promise with data: {
 *      message: <english-message>
 *      errorCode: <ERROR_CODE>
 *   }
 */
export function validateUserPasswordField(uid, password) {

    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.USER],
        'validate-user-password-field'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                uid,
                password
            }
        }
    );
}

export function validateSysUserPasswordField(uid, password) {

    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.SYSTEM_USER],
        'validate-user-password-field'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                uid,
                password
            }
        }
    );
}


export function validateAdminUserPasswordField(uid, password) {

    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.ADMINISTRATOR],
        'validate-user-password-field'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                uid,
                password
            }
        }
    );
}


/**
 * Gets default values of community's send summary mail
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function getCommunitySummaryMail(communityName) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.COMMUNITY],
        'get-summary-email'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: communityName
            }
        }
    );
}

/**
 * Sends community summary mail
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object<String:id, Boolean:success, String:errorCode>>: Results of action>  
 *   }
 */
export function sendCommunitySummaryMail(communityName, subject, to, bcc, body, captchaKey) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.COMMUNITY],
        'send-summary-email'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: communityName,
                subject,
                to,
                bcc,
                body,
                captchaKey
            }
        }
    );
}

/**
 * Fetches members of community
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function getCommunityMembers(commName) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.COMMUNITY],
        ApiFunctions.GET_MEMBERS
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: commName
            }
        },
    );
}

/**
 * Fetches list of communities that can be members of community
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function getPossibleCommunityMembersList(commName) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.COMMUNITY],
        'get-select-community-list'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: commName
            }
        },
    );
}

/**
 * Fetches members of community
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function addGatewaysToCommunityMembers(communityName, gatewayNames) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.COMMUNITY],
        'add-gateways-to-members'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                communityName: communityName,
                gatewayNameList: Array.isArray(gatewayNames) ? gatewayNames : [gatewayNames]
            }
        },
    );
}

/**
 * Fetches members of community
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function addCommunityToCommunityMembers(communityName, communityToAdd) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.COMMUNITY],
        'add-community-to-members'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                communityName: communityName,
                chosenCommunityName: communityToAdd
            }
        },
    );
}

/**
 * Fetches members of community
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function removeCommunityMembers(communityName, membersToRemove) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.COMMUNITY],
        'remove-members'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                communityName: communityName,
                memberList: membersToRemove
            }
        },
    );
}

/**
 * Fetches members of community
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function toExcelStart(objectType, objectList) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.EXCEL,
        'start'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                objectType: objectType,
                nameList: objectList
            }
        },
    );
}

/**
 * Fetches members of community
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function toExcelGetProgress(taskId) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.EXCEL,
        'get-progress'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: taskId
            }
        },
    );
}

/**
 * Fetches members of community
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function toExcelDone(taskId) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.EXCEL,
        'done'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: taskId
            },
            timeout: 180000 // Timeout of 3 minutes for this request
        },
        {'Accept': 'application/octet-stream'}
    );
}

/**
 * Fetches members of community
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function logsToExcelStart(objectType, filterObject) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.EXCEL_LOGS,
        'start'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                objectType: objectType,
                filter: filterObject
            }
        },
    );
}

/**
 * Fetches members of community
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function logsToExcelGetProgress(taskId) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.EXCEL_LOGS,
        'get-progress'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: taskId
            }
        },
    );
}

/**
 * Fetches members of community
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function logsToExcelDone(taskId) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.EXCEL_LOGS,
        'done'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: taskId
            },
            timeout: 180000 // Timeout of 3 minutes for this request
        },
        {'Accept': 'application/octet-stream'}
    );
}

/**
 * Fetches members of community
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function generateDomainReports() {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.EXCEL,
        'generate-domain-reports'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {},
            timeout: 180000 // Timeout of 3 minutes for this request
        },
        {'Accept': 'application/octet-stream'}
    );
}

/**
 * Generate system excel report
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      members: <Array<Object:MemberData>>  
 *   }
 */
export function generateSystemReports() {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.EXCEL,
        'generate-system-reports'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {},
            timeout: 0 // Indefinite timeout
        },
        {'Accept': 'application/octet-stream'}
    );
}

/**
 * Starts the action of exporting the SMP DB file
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *   }
 */
export function exportDBFileStart(data) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.SYSTEM,
        ApiPaths.TOOLS,
        'get-db-file-start'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        { data: { data } },
    );
}

/**
 * Fetches progress data of an SMP DB file export action
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *   }
 */
export function exportDBFileGetProgress(taskId) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.SYSTEM,
        ApiPaths.TOOLS,
        'get-db-file-progress'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: taskId
            }
        },
    );
}

/**
 * Stops an SMP DB file export action
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *   }
 */
export function exportDBFileStop(taskId) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.SYSTEM,
        ApiPaths.TOOLS,
        'get-db-file-stop'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: taskId
            }
        },
    );
}

/**
 * Fetches the finished data of an SMP DB file export action
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *   }
 */
export function exportDBFileDone(taskId) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.SYSTEM,
        ApiPaths.TOOLS,
        'get-db-file-done'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: taskId
            },
            timeout: 600000 // Timeout of 10 minutes for this request
        },
        {'Accept': 'application/octet-stream'}
    );
}

/**
 * Starts the action of exporting the SMP DB file
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *   }
 */
 export function exportDomainDBFileStart() {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.PORTAL,
        ApiPaths.SETTINGS,
        'get-db-file-start'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
    );
}

/**
 * Starts the action of importing the SMP DB file
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *   }
 */
export function importDBFileStart(jsonData, fileData) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.SYSTEM,
        ApiPaths.TOOLS,
        'set-db-file-start'
    ]);

    const formData = new FormData();
    formData.append('file', fileData);
    formData.append('json', JSON.stringify(jsonData));

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: formData
        },
        {'Content-Type': 'multipart/form-data'}
    );
}

/**
 * Fetches progress data of an SMP DB file import action
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *   }
 */
export function importDBFileGetProgress(taskId) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.SYSTEM,
        ApiPaths.TOOLS,
        'set-db-file-progress'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: taskId
            }
        },
    );
}

/**
 * Stops an SMP DB file import action
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *   }
 */
export function importDBFileStop(taskId) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.SYSTEM,
        ApiPaths.TOOLS,
        'set-db-file-stop'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: taskId
            }
        },
    );
}

/**
 * Fetches the finished data of an SMP DB file import action
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *   }
 */
export function importDBFileDone(taskId) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.SYSTEM,
        ApiPaths.TOOLS,
        'get-db-import-file-done'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: taskId
            }
        }
    );
}

/**
 * Starts the action of importing the SMP DB file
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *   }
 */
 export function importDomainDBFileStart(jsonData, fileData) {
    const createObjectPath = buildAPIRequestPath([
        ApiPaths.PORTAL,
        ApiPaths.SETTINGS,
        'set-db-file-start'
    ]);

    const formData = new FormData();
    formData.append('file', fileData);
    formData.append('json', JSON.stringify(jsonData));

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: formData
        },
        {'Content-Type': 'multipart/form-data'}
    );
}



/**
 * Does an action
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function doAction(objectType, action, nameList, data) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        action
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                nameList,
                ...data
            }
        }
    );
}

/**
 * Gets current sessions from SMP
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function getSessions() {
    return fetchFromSMP(
        'get-overview-sessions',
        fetchFromSMPMethods.POST,
        {
            data: {}
        }
    );
}

/**
 * Sends user a 2FA mail with their QR code from the SMP
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function sendUserTwoFAMail(objectType, userId, userEmail, captchaKey) {
    const createObjectPath = buildAPIRequestPath([
        '2fa',
        'send-email'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                userType: objectType,
                name: userId,
                userEmail: userEmail,
                captchaKey: captchaKey
            }
        }
    );
}

/**
 * Reinitializes System Certificate authority
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function reinitializeSystemCaStart() {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.SYSTEM_SETTINGS],
        'ca-command-helper',
        'start'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                commandName: 'REVOKE_CA'
            }
        }
    );
}

/**
 * Reinitializes System Certificate authority
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function reinitializeSystemCaGetProgress(taskId) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.SYSTEM_SETTINGS],
        'ca-command-helper',
        'progress'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                taskID: taskId
            }
        }
    );
}

/**
 * Reinitializes System Certificate authority
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function reinitializeSystemCaStop(taskId) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.SYSTEM_SETTINGS],
        'ca-command-helper',
        'stop'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                taskID: taskId,
            }
        }
    );
}

/**
 * Requests a new service domain
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function requestNewServiceDomain(portalName, domainGoal, country, expectedGWs, firstName, lastName, email, userCenter, captchaKey) {
    const createObjectPath = buildAPIRequestPath([
        'new-domain-request',
        'set'
    ]);
    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                portalName,
                domainGoal,
                country,
                expectedGWs,
                firstName,
                lastName,
                email,
                userCenter,
                captchaKey
            }
        }
    );
}

/**
 * Gets list of download center firmwares
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function getDcFirmwareList() {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.FIRMWARE],
        'get-dc-firmware-list'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {},
            timeout: 180000 // Timeout of 3 minutes for this request
        }
    );
}

/**
 * Gets list of firmwares by version
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function getFirmwareListByVersion(getHidden, getDisabled) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.FIRMWARE],
        'get-list-by-version'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                getHidden,
                getDisabled
            },
        }
    );
}

/**
 * Change Firmaware from Customer Hotfix to Check point General Availabillity
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
 export function SetFirmwareCustomerHotfixToGA(
        objectType,
        objectName,
       data
) {
    const updateObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.UPDATE_FIRMWARE
    ]);

    return fetchFromSMP(
        updateObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: objectName,
                data: data
            }
        }
    );
}

/**
 * Change Firmaware to recommended
 *
 * @returns Promise with data: {
 *      message: <english-message>
 *      errorCode: <ERROR_CODE>
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>
 *   }
 */
export function SetFirmwareAsRecommended(
    objectType,
    objectName,
) {
    const updateObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[objectType],
        ApiFunctions.UPDATE_FIRMWARE_RECOMMENDED
    ]);

    return fetchFromSMP(
        updateObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: objectName
            }
        }
    );
}

/**
 * Creates new firmwares on SMP
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function createFirmwares(firmwares) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.FIRMWARE],
        'create-firmware'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                nameList: firmwares
            },
            timeout: 120000 // Timeout of 2 minutes for this request
        }
    );
}

/**
 * Adds firmware to service domain as a private firmware
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function createPrivateFirmware(firmwareId, serviceDomainList) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.FIRMWARE],
        'create-private-firmware'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                firmwareId: firmwareId,
                serviceDomainList: serviceDomainList
            },
            timeout: 120000 // Timeout of 2 minutes for this request
        }
    );
}

/**
 * Adds firmware to this service domain as a private firmware
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function addPrivateFirmwareToThisPortal(firmwareId) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.PORTAL_SETTINGS],
        'set-firmware-by-id'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                id: firmwareId
            },
            timeout: 120000 // Timeout of 2 minutes for this request
        }
    );
}

/**
 * Creates an image file on the SMP
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function createImage(imageName, imageData) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.IMAGE],
        ApiFunctions.CREATE
    ]);

    const jsonData = { name: imageName };
    
    const formData = new FormData();
    formData.append('file', imageData);
    formData.append('json', JSON.stringify(jsonData));
    

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: formData,
            timeout: 120000 // Timeout of 2 minutes for this request
        },
        {'Content-Type': 'multipart/form-data'}
    );
}

/**
 * Creates an image file on the SMP for report custom logo
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */

export function uploadReportCustomLogo(imageName, imageData) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.IMAGE],
        'upload-report-custom-logo'
    ]);

    const jsonData = { name: imageName };
    
    const formData = new FormData();
    formData.append('file', imageData);
    formData.append('json', JSON.stringify(jsonData));
    

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: formData,
            timeout: 120000 // Timeout of 2 minutes for this request
        },
        {'Content-Type': 'multipart/form-data'}
    );
}

/**
 * Reads image data from SMP and return byte data
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function readImageData(imageName) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.IMAGE],
        'read-data'
    ]);


    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                name: imageName
            },
            timeout: 60000 // Timeout of 60 seconds for this request
        },
        {'Accept': 'application/octet-stream'}
    );
}

/**
 * Reads updatable-objects list from SMP
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function getUpdatableObjects(){
    return fetchFromSMP(
        'get-updatable-objects',
        fetchFromSMPMethods.POST,
        {
            data:{},
        }
    );
}

/**
 * Reads zone list from SMP
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
 export function getZoneList() {
    return fetchFromSMP(
        'get-zone-list',
        fetchFromSMPMethods.POST,
        {
            data: {},
        }
    );
}

/**
 * Reads appi application list from SMP
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function getAppiOtherApplicationsList(version) {
    return fetchFromSMP(
        'get-appi-other-applications',
        fetchFromSMPMethods.POST,
        {
            data: {
                version: version
            }
        }
    );
}

/**
 * Reads appi application list from SMP
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function getSmartviewDataUrl(
    viewName,
    filterLevel,
    entityId,
    isSmartLog,
    additionalFilter,
    gatewayName,
    referer
) {
    return fetchFromSMP(
        'get-smartview-data-url',
        fetchFromSMPMethods.POST,
        {
            data: {
                viewName,
                filterLevel,
                entityId,
                isSmartLog,
                additionalFilter,
                gatewayName,
                referer
            },
        }
    );
}

/**
 * Creates a CA certificate file on the SMP
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function createCaCertificate(certName, certFile, dnName, isNew) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.PORTAL_SETTINGS],
        'set-ca-cert-by-name'
    ]);

    const jsonData = {
        certName: certName,
        dnName: dnName,
        isNew: isNew
    };
    
    const formData = new FormData();
    formData.append('file', certFile);
    formData.append('json', JSON.stringify(jsonData));
    

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: formData,
            timeout: 60000 // Timeout of 60 seconds for this request
        },
        {'Content-Type': 'multipart/form-data'}
    );
}

/**
 * Deletes a CA certificate from the SMP
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function deleteCaCertificate(dnName) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.PORTAL_SETTINGS],
        'delete-ca-cert-by-name'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                dnName: dnName
            },
            timeout: 60000 // Timeout of 60 seconds for this request
        },
        {'Content-Type': 'multipart/form-data'}
    );
}

/**
 * Tests the connectivity of a radius server from the SMP server
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function testRadiusServer(
    radiusServerType,
    radiusHostIP,
    radiusHostSecret,
    radiusPort,
    radiusUserName,
    radiusUserPassword
) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.PORTAL_SETTINGS],
        'test-radius-auth'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                radiusServerType: radiusServerType,
                radiusHostIP: radiusHostIP,
                radiusHostSecret: radiusHostSecret,
                radiusPort: radiusPort,
                radiusUserName: radiusUserName,
                radiusUserPassword: radiusUserPassword
            },
            timeout: 30000 // Timeout of 30 seconds for this request
        },
    );
}

/**
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function getSystemOverviewData() {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.SYSTEM_SETTINGS],
        'get-overview'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {},
            timeout: 60000 // Timeout of 60 seconds for this request
        }
    );
}

/**
 * Deletes a CA certificate from the SMP
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function getLicenseManagementList(licensesList) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.SYSTEM_SETTINGS],
        'get-license-management-list'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                data: {
                    licensesList: licensesList
                }
            },
        }
    );
}

/**
 * Deletes a CA certificate from the SMP
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function setLicenseManagementList(dnName) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.SYSTEM_SETTINGS],
        'set-license-management'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {},
        }
    );
}

/**
 * Cancels a task
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function cancelTasks(indexList) {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.ACTIVITY_LOGS],
        'cancel-task'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {
                indexList: indexList
            },
        }
    );
}

/**
 * Clears finished tasks from activity logs
 *
 * @returns Promise with data: {  
 *      message: <english-message>  
 *      errorCode: <ERROR_CODE>  
 *      results: <Array<Object:{id:string, success:boolean, errorCode:string}>>  
 *   }
 */
export function clearFinishedTasksFromActivityLogs() {
    const createObjectPath = buildAPIRequestPath([
        objectTypeToApiPath[ObjectTypes.ACTIVITY_LOGS],
        'clear-completed-tasks'
    ]);

    return fetchFromSMP(
        createObjectPath,
        fetchFromSMPMethods.POST,
        {
            data: {},
        }
    );
}
